/**
 * CooperadExplainer — progressive-disclosure explainer for cooperads.
 *
 * A cooperad reverses the arrows of an operad: one input is decomposed into
 * many outputs. In CSK this powers the DECOMPOSE (De) Compositionality
 * Principle, which proves that safe composition and safe extraction are
 * structurally independent — silent extraction that inherits the whole's
 * safety certificate is inexpressible by construction.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";
import { COMPOSITIONALITY_PRINCIPLE_COLOR, THEOREM_COLOR } from "../../design-system/tokens.js";
import { MathInline, tex } from "../../components/math/index.js";
import {
  ExplainerCard,
  type ExplainerCrossLink,
  type ExplainerIdentity,
  type ExplainerTierContent,
} from "../shared/index.js";
import {
  ComposeDecomposeAdjunction,
  CooperadicDecompositionAnimation,
  CooperadicTree,
} from "./operad/index.js";

const ACCENT = COMPOSITIONALITY_PRINCIPLE_COLOR.DECOMPOSE.color;
const COMPOSE_ACCENT = THEOREM_COLOR.COMPOSE;

const IDENTITY: ExplainerIdentity = {
  id: "explainer-cooperad",
  kind: "Categorical structure",
  title: "Cooperad",
  subtitle: "Q(0), Q(1), Q(2), … with Δ : Q(k₁+…+kₙ) → Q(n) ⊗ Q(k₁) ⊗ … ⊗ Q(kₙ)",
  accent: ACCENT,
};

const CROSS_LINKS: readonly ExplainerCrossLink[] = [
  { label: "Operad", href: "#explainer-operad", accent: COMPOSE_ACCENT },
  { label: "Bioperad", href: "#explainer-bioperad" },
  { label: "Adjunction", href: "#explainer-adjunction" },
  { label: "F-Coalgebra", href: "#explainer-fcoalgebra" },
];

function CooperadDiagramStrip(): React.JSX.Element {
  return (
    <div
      style={{
        display: "grid",
        gap: "0.75rem",
        gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))",
      }}
    >
      <CooperadicTree accent={ACCENT} />
      <CooperadicDecompositionAnimation accent={ACCENT} />
      <ComposeDecomposeAdjunction composeAccent={COMPOSE_ACCENT} decomposeAccent={ACCENT} />
    </div>
  );
}

const TIERS: ExplainerTierContent = {
  tldr: (
    <p>
      A <strong>cooperad</strong> is an operad with the arrows reversed: instead of plugging many
      operations into one, it splits one composite into many parts — along with explicit data about
      how those parts depended on each other. CSK uses cooperads to make "safe extraction" a
      different problem from "safe composition," so claiming that a sub-component inherits the
      whole's safety becomes structurally inexpressible without a witness.
    </p>
  ),
  intuition: (
    <>
      <p>
        An operad builds. A cooperad <em>takes apart</em>. If the operad's picture is a tree growing
        upward — many inputs converging into one output — the cooperad's picture is the same tree
        read top-down: one input, branching into many parts.
      </p>
      <p>
        The subtle point is that the parts are not independent. A sauce extracted from a finished
        plate is not the sauce by itself — it carries an annotation that says "this part depended on
        the pasta beneath it." A cooperad's decomposition <MathInline>{tex`\Delta`}</MathInline>{" "}
        always carries that annotation, called the <strong>residual dependency</strong>. The
        cooperad is the structure that makes the dependency tracking <em>mandatory</em>: there is no
        decomposition that hides it.
      </p>
      <p>
        For security: knowing that a composite system is safe does not automatically mean every
        sub-system you extract from it is safe in isolation. The cooperad makes that gap explicit
        and refuses to let a caller paper over it.
      </p>
    </>
  ),
  formal: (
    <>
      <p>
        A <strong>cooperad</strong> <MathInline>{tex`Q`}</MathInline> in a symmetric monoidal
        category <MathInline>{tex`(C, \otimes, I)`}</MathInline> is the formal dual of an operad:
      </p>
      <ul>
        <li>
          A family of objects <MathInline>{tex`Q(n)`}</MathInline>.
        </li>
        <li>
          A <strong>counit</strong> <MathInline>{tex`\varepsilon : Q(1) \to I`}</MathInline>.
        </li>
        <li>
          A <strong>decomposition</strong>{" "}
          <MathInline ariaLabel="Δ from Q(k) to the direct sum of Q(n) tensor Q(k1) tensor ... tensor Q(kn)">
            {tex`\Delta : Q(k) \to \bigoplus Q(n) \otimes Q(k_{1}) \otimes \cdots \otimes Q(k_{n})`}
          </MathInline>{" "}
          ranging over trees with <em>n</em> outer slots and total leaf count{" "}
          <MathInline>{tex`k`}</MathInline> (the sum is taken over all ways of partitioning the{" "}
          <em>k</em> inputs into <em>n</em> groups of sizes{" "}
          <MathInline>{tex`k_{1} + \cdots + k_{n} = k`}</MathInline>).
        </li>
        <li>
          A symmetric-group action on <MathInline>{tex`Q(n)`}</MathInline>, equivariant with{" "}
          <MathInline>{tex`\Delta`}</MathInline>.
        </li>
      </ul>
      <p>
        The two coherence laws — <strong>coassociativity</strong> of{" "}
        <MathInline>{tex`\Delta`}</MathInline> and the <strong>counit</strong> law — are the mirror
        images of operad associativity and unit. Concretely: decomposing a composite then
        decomposing each piece agrees with decomposing once into smaller trees; decomposing a
        single-slot operation through <MathInline>{tex`\varepsilon`}</MathInline> gives the unit
        back.
      </p>
      <p>
        Every operad <MathInline>{tex`P`}</MathInline> has a <strong>Koszul dual</strong> cooperad{" "}
        <MathInline>{tex`P^{!}`}</MathInline> defined by a bar/cobar adjunction. For Koszul operads
        — and CSK's COMPOSE operad is one — the Koszul dual is a fully constructive description of
        "how a P-composite decomposes."
      </p>
    </>
  ),
  relevance: (
    <>
      <p>
        The cooperad is the structural carrier of CSK's <strong>DECOMPOSE</strong> (De)
        Compositionality Principle. DECOMPOSE proves that{" "}
        <em>safe composition is not safe extraction</em>: deploying COMPOSE does not make a
        vulnerability class that another theorem rendered structurally inexpressible expressible
        again, and pulling apart a safe composite does not silently inherit a safety certificate.
      </p>
      <ul>
        <li>
          <strong>Operad ⊣ cooperad</strong> is a level-2 SDP. The unit and counit of the bar/cobar
          adjunction witness that every operadic composition has a cooperadic decomposition, and
          every cooperadic decomposition recomposes — but only with the residual dependency data
          carried along.
        </li>
        <li>
          <strong>Auditability.</strong> The decomposition <MathInline>{tex`\Delta`}</MathInline>{" "}
          always emits dependency annotations; an extraction that drops them is not in the cooperad.
          There is no closed term for "silent split."
        </li>
        <li>
          <strong>ICG-De.</strong> The Immunity Closure Guarantee pairs COMPOSE (C) with DECOMPOSE
          (De) into one Safety Envelope: a single auditable certificate that both safe composition
          and safe extraction hold, with the unit/counit as structural witnesses.
        </li>
      </ul>
      <p>Examples of attacks the cooperad neutralizes:</p>
      <ul>
        <li>
          <strong>Library extraction without provenance.</strong> Pulling a "safe" sub-library out
          of a verified binary and reusing it without acknowledging the linker context it depended
          on. The cooperadic Δ refuses to forget the linker context.
        </li>
        <li>
          <strong>Confused deputy across module boundaries.</strong> If a sub-module's safety was
          contingent on the outer module's authorization predicate, extraction must carry that
          predicate; the cooperad does not admit a witness that drops it.
        </li>
        <li>
          <strong>Latent capability leakage.</strong> A capability composed inside a sandboxed tree
          must, on extraction, surface its full dependency on the sandbox — there is no cooperadic
          decomposition that quietly delivers the capability to the outside world.
        </li>
      </ul>
    </>
  ),
  references: [
    {
      label: "Theorem Canon — DECOMPOSE (De)",
      path: "docs/reference/Theorem-Canon.md",
      note: "§7 SDP table: operad ↔ cooperad duality (level 2); §7a ICG-De Safety Envelope.",
    },
    {
      label: "DECOMPOSE Compositionality Principle (Agda)",
      path: "agda/Kernel/CompositionalityPrinciple/DECOMPOSE.agda",
      note: "Mechanized SDP: composition and extraction directions are structurally independent.",
    },
    {
      label: "Cooperad Koszul duality (Agda)",
      path: "agda/Kernel/CompositionalityPrinciple/DECOMPOSE/CooperadDuality.agda",
      note: "Bar/cobar construction; explicit dependency annotations on extraction.",
    },
    {
      label: "Decompose-cooperad research note (Agda)",
      path: "agda/Research/CompositionPrinciples/DecomposeCooperad.agda",
      note: "RD-71 Koszul-duality analysis for the COMPOSE/DECOMPOSE pair.",
    },
    {
      label: "Composition governance witnesses (Agda)",
      path: "agda/Foundation/Governance/CompositionGovernanceWitnesses.agda",
      note: "Evidence-of-safe-decomposition records used by the governance layer.",
    },
  ],
};

export function CooperadExplainer(): React.JSX.Element {
  return (
    <ExplainerCard
      identity={IDENTITY}
      tiers={TIERS}
      diagram={<CooperadDiagramStrip />}
      crossLinks={CROSS_LINKS}
    />
  );
}
